import { Banknote, Pencil, Trash2, ZoomIn } from 'lucide-react';
import { ExpenseAttachmentButton } from './ExpenseAttachmentButton';
import {
  type ExpenseItemWithRelations,
  type ExpenseListSort,
  type User,
  isAdmin,
  isPositiveAmount,
  formatCurrency,
} from '@peles/shared';
import { StatusPill } from '../common';
import { EntityList, type EntityListColumn, type SortDirection } from '../common/EntityList';
import { useDialog } from '../../context/DialogContext';
import { useExpenseDocuments } from '../../hooks/useExpenseDocuments';
import { DocumentPreviewPicker } from '../documents/DocumentPreviewPicker';
import { RecordConversations } from '../messaging/RecordConversations';
import { MoneyStateBadge } from './money/MoneyStateBadge';
import { describeNextDue } from './money/moneyDisplay';

export const EXPENSE_PAGE_SIZES = [25, 50, 100];

interface ExpenseTableProps {
  items: ExpenseItemWithRelations[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onDelete?: (id: string) => void;
  /** When set, rows show a zoom button that opens the itemized list scoped to that expense. */
  onZoomItems?: (item: ExpenseItemWithRelations) => void;
  /** When set, a linked vendor's name is a button that opens its review (contacts, payment). */
  onVendorClick?: (item: ExpenseItemWithRelations) => void;
  /** When set, a line with something owed offers "Record payment". */
  onRecordPayment?: (item: ExpenseItemWithRelations) => void;
  isLoading: boolean;
  currentUser?: User;
  showEventName?: boolean;
  /**
   * This page's conversation counts, keyed by expense id.
   *
   * ⚠️ **Supplied by the page, never fetched per row.** `useThreadCounts` asks
   * once for the whole list; a row that fetched for itself would put a request
   * on the wire per line, and another set on every page change. `null` for a
   * row means "not known yet", which renders no badge rather than a `0`.
   */
  threadCountFor?: (expenseItemId: string) => { total: number; unread: number } | null;
  /** Server paging: the page on screen, its size, and how many lines match. */
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  /** Server sort: the column and direction, both null for the list's own order. */
  sortBy: ExpenseListSort | null;
  sortDir: SortDirection | null;
  onSortChange: (sortBy: ExpenseListSort | null, sortDir: SortDirection | null) => void;
  emptyMessage?: string;
}

/**
 * The next thing owed on a line — its first installment with something left,
 * or its single due date — red once it is overdue (the server applied the
 * payments against the office's today).
 */
function NextDue({ item }: { item: ExpenseItemWithRelations }) {
  return (
    <span className={item.money.overdue ? 'text-red-600' : 'text-gray-600'}>
      {describeNextDue(item.money)}
    </span>
  );
}

function getClientStatusBadge(item: ExpenseItemWithRelations): {
  label: string;
  className: string;
  title: string;
} | null {
  if (!item.isProducerFunded) return null;
  const balance = item.clientBalance;
  if (!isPositiveAmount(balance)) {
    return {
      label: 'paid by client',
      className: 'bg-green-100 text-green-700',
      title: `Client has paid ${formatCurrency(item.clientPaid)} toward this expense.`,
    };
  }
  if (isPositiveAmount(item.clientPaid)) {
    return {
      label: `partial • ${formatCurrency(balance)} owed`,
      className: 'bg-amber-100 text-amber-700',
      title: `Client has paid ${formatCurrency(item.clientPaid)} of ${formatCurrency(item.price)}.`,
    };
  }
  return {
    label: `${item.clientChargeType === 'income' ? 'income' : 'reimbursement'} • ${formatCurrency(balance)} owed`,
    className: 'bg-red-100 text-red-700',
    title:
      item.clientChargeType === 'income'
        ? 'Owed to producer for production services.'
        : 'Producer fronted this; awaiting client reimbursement.',
  };
}

export interface ExpenseDocPreviewProps {
  expenseItemId: string;
  onClose: () => void;
}

export function ExpenseDocPreview({ expenseItemId, onClose }: ExpenseDocPreviewProps) {
  const { data: docs = [], isLoading } = useExpenseDocuments(expenseItemId);

  return <DocumentPreviewPicker documents={docs} isLoading={isLoading} onClose={onClose} />;
}

function EventCell({
  item,
  compact = false,
}: {
  item: ExpenseItemWithRelations;
  compact?: boolean;
}) {
  return (
    <div className={`flex items-center gap-2 ${compact ? 'text-xs text-gray-700' : ''}`}>
      {item.eventLogoUrl && (
        <img
          src={item.eventLogoUrl}
          alt=""
          className={`${compact ? 'w-5 h-5' : 'w-6 h-6'} rounded object-cover border border-gray-200 bg-white flex-shrink-0`}
        />
      )}
      <span className={`truncate ${compact ? 'font-medium' : ''}`}>{item.eventName || '—'}</span>
    </div>
  );
}

function Money({ amount, owed = false }: { amount: number; owed?: boolean }) {
  return (
    <span
      className={
        owed ? (isPositiveAmount(amount) ? 'text-red-600 font-medium' : 'text-green-600') : ''
      }
    >
      {formatCurrency(amount)}
    </span>
  );
}

/**
 * The expenses list: the shared `EntityList` (sortable headers, paging, page
 * sizes, skeleton rows, a card per line on a phone), paged and sorted by the
 * server. Every action on a line is a button of its own, never hover-only.
 */
export function ExpenseTable({
  items,
  selectedId,
  onSelect,
  onDelete,
  onZoomItems,
  onVendorClick,
  onRecordPayment,
  isLoading,
  currentUser,
  showEventName = false,
  threadCountFor,
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  sortBy,
  sortDir,
  onSortChange,
  emptyMessage = 'No expenses found',
}: ExpenseTableProps) {
  const { confirm } = useDialog();
  const canDelete = !!currentUser && isAdmin(currentUser.systemRole) && !!onDelete;

  const askDelete = async (item: ExpenseItemWithRelations) => {
    if (
      await confirm({
        title: 'Delete Expense',
        message: 'Delete this expense item? This action cannot be undone.',
        confirmLabel: 'Delete',
      })
    ) {
      onDelete?.(item.id);
    }
  };

  /** The vendor's name — a button onto its review when the line links a vendor record. */
  const vendorName = (item: ExpenseItemWithRelations) => {
    if (!item.vendorName) return <span>—</span>;
    if (!onVendorClick || !item.vendorId) return <span>{item.vendorName}</span>;
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onVendorClick(item);
        }}
        className="min-h-11 sm:min-h-0 inline-flex items-center text-left text-primary-600 hover:text-primary-700 hover:underline"
        title="Vendor contacts and payment methods"
      >
        {item.vendorName}
      </button>
    );
  };

  const conversation = (item: ExpenseItemWithRelations) =>
    threadCountFor ? (
      <RecordConversations
        entityType="ExpenseItem"
        entityId={item.id}
        variant="icon"
        count={threadCountFor(item.id)}
      />
    ) : null;

  /** A line's actions, as icons in the table and labelled buttons on a card. */
  const actions = (item: ExpenseItemWithRelations, labelled: boolean) => {
    const buttonClass = labelled
      ? 'min-h-11 flex items-center gap-1.5 px-3 text-sm text-gray-600 rounded-lg border border-gray-200 hover:bg-gray-50'
      : 'min-h-8 min-w-8 flex items-center justify-center text-gray-400 hover:text-primary-600 rounded-lg';
    return (
      <div
        className={`flex items-center ${labelled ? 'flex-wrap gap-2' : 'gap-1'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {onRecordPayment && isPositiveAmount(item.balance) && (
          <button
            type="button"
            onClick={() => onRecordPayment(item)}
            className={buttonClass}
            aria-label="Record payment"
            title="Record payment"
          >
            <Banknote size={16} />
            {labelled && 'Pay'}
          </button>
        )}
        <ExpenseAttachmentButton expenseItemId={item.id} size={16} />
        {onZoomItems && (
          <button
            type="button"
            onClick={() => onZoomItems(item)}
            className={buttonClass}
            aria-label="View itemized list"
            title="View linked planning items"
          >
            <ZoomIn size={16} />
            {labelled && 'Items'}
          </button>
        )}
        <button
          type="button"
          onClick={() => onSelect(item.id)}
          className={buttonClass}
          aria-label="Edit expense"
          title="Edit expense"
        >
          <Pencil size={16} />
          {labelled && 'Edit'}
        </button>
        {canDelete && (
          <button
            type="button"
            onClick={() => void askDelete(item)}
            className={`${buttonClass} hover:text-red-600`}
            aria-label="Delete expense"
            title="Delete expense"
          >
            <Trash2 size={16} />
            {labelled && 'Delete'}
          </button>
        )}
      </div>
    );
  };

  const columns: EntityListColumn<ExpenseItemWithRelations>[] = [
    ...(showEventName
      ? [
          {
            key: 'event',
            header: 'Event',
            sortable: true,
            render: (item: ExpenseItemWithRelations) => <EventCell item={item} />,
          },
        ]
      : []),
    {
      key: 'category',
      header: 'Category',
      sortable: true,
      render: (item) => (
        <span className="bg-primary-100 text-primary-700 text-xs px-2 py-0.5 rounded whitespace-nowrap">
          {item.vendorCategoryName}
        </span>
      ),
    },
    {
      key: 'item',
      header: 'Item',
      sortable: true,
      render: (item) => <span className="font-medium">{item.itemName || '—'}</span>,
    },
    {
      key: 'vendor',
      header: 'Vendor',
      sortable: true,
      render: (item) => (
        <span className="flex items-center gap-1.5 text-gray-600">
          {vendorName(item)}
          {conversation(item)}
        </span>
      ),
    },
    {
      key: 'price',
      header: 'Price',
      align: 'right',
      sortable: true,
      render: (item) => <Money amount={item.price} />,
    },
    {
      key: 'paid',
      header: 'Paid',
      align: 'right',
      render: (item) => <span className="text-gray-600">{formatCurrency(item.totalPaid)}</span>,
    },
    {
      key: 'balance',
      header: 'Balance',
      align: 'right',
      render: (item) => <Money amount={item.balance} owed />,
    },
    {
      key: 'money',
      header: 'Paid?',
      render: (item) => <MoneyStateBadge money={item.money} />,
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (item) => <StatusPill label={item.statusName} color={item.statusColor} />,
    },
    {
      key: 'client',
      header: 'Client',
      render: (item) => {
        const badge = getClientStatusBadge(item);
        if (!badge) return <span className="text-xs text-gray-400">—</span>;
        return (
          <span className={`text-xs px-2 py-0.5 rounded ${badge.className}`} title={badge.title}>
            {badge.label}
          </span>
        );
      },
    },
    {
      key: 'dueDate',
      header: 'Next due',
      sortable: true,
      render: (item) => <NextDue item={item} />,
    },
    { key: 'actions', header: 'Actions', render: (item) => actions(item, false) },
  ];

  const renderCard = (item: ExpenseItemWithRelations) => (
    <div className="p-3 space-y-2">
      {showEventName && (item.eventName || item.eventLogoUrl) && <EventCell item={item} compact />}
      <div className="flex justify-between items-start gap-2">
        <span className="font-medium text-sm">{item.itemName || item.vendorCategoryName}</span>
        <span className="flex items-center gap-1 shrink-0">
          <MoneyStateBadge money={item.money} />
          <StatusPill label={item.statusName} color={item.statusColor} />
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
        <span className="bg-primary-100 text-primary-700 px-2 py-0.5 rounded">
          {item.vendorCategoryName}
        </span>
        {item.vendorName && vendorName(item)}
        {conversation(item)}
      </div>
      <dl className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
        <div>
          <dt className="text-gray-500">Price</dt>
          <dd className="font-medium text-gray-900">{formatCurrency(item.price)}</dd>
        </div>
        <div>
          <dt className="text-gray-500">Paid</dt>
          <dd className="text-gray-700">{formatCurrency(item.totalPaid)}</dd>
        </div>
        <div>
          <dt className="text-gray-500">Balance</dt>
          <dd>
            <Money amount={item.balance} owed />
          </dd>
        </div>
      </dl>
      {item.money.nextDue && (
        <p className="text-xs">
          <span className="text-gray-500">Next due: </span>
          <NextDue item={item} />
        </p>
      )}
      <div className="pt-2 border-t border-gray-100">{actions(item, true)}</div>
    </div>
  );

  return (
    <EntityList
      items={items}
      columns={columns}
      getKey={(item) => item.id}
      isLoading={isLoading}
      selectedId={selectedId}
      onRowClick={(item) => onSelect(item.id)}
      renderCard={renderCard}
      page={page}
      pageSize={pageSize}
      total={total}
      onPageChange={onPageChange}
      pageSizeOptions={EXPENSE_PAGE_SIZES}
      onPageSizeChange={onPageSizeChange}
      sortBy={sortBy}
      sortDir={sortDir}
      onSortChange={(key, dir) => onSortChange(key as ExpenseListSort | null, dir)}
      emptyMessage={emptyMessage}
    />
  );
}
